// The device's own font everywhere, as on the website: nothing to download.
import "./styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Shell } from "./components/Shell.tsx";
import { Loading, ToastProvider } from "./components/ui.tsx";
import { ApiError } from "./lib/api.ts";
import { MeProvider, useSession } from "./lib/session.tsx";
import { Duplicates } from "./screens/Duplicates.tsx";
import { Imports, ImportRun } from "./screens/Imports.tsx";
import { Inbox, Thread } from "./screens/Inbox.tsx";
import { Listing, Listings } from "./screens/Listings.tsx";
import { More } from "./screens/More.tsx";
import { People } from "./screens/People.tsx";
import { Person } from "./screens/Person.tsx";
import { Pipelines } from "./screens/Pipelines.tsx";
import { Properties, Property } from "./screens/Properties.tsx";
import { Settings } from "./screens/Settings.tsx";
import { DevicePending, SignIn } from "./screens/SignIn.tsx";
import { Tasks } from "./screens/Tasks.tsx";
import { Today } from "./screens/Today.tsx";

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      retry: (n, err) => !(err instanceof ApiError && err.status < 500) && n < 2,
      networkMode: "offlineFirst",
    },
    mutations: { networkMode: "always" },
  },
});

function App() {
  const s = useSession();
  if (s.isLoading) return <Loading />;
  if (!s.data) return <SignIn />;
  if (!s.data.device.approved) return <DevicePending me={s.data} />;
  const me = s.data;
  const staff = me.user.role !== "agent", owner = me.user.role === "owner";
  return (
    <MeProvider me={me}>
      <Shell>
        <Routes>
          <Route path="/" element={<Today />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/inbox/:id" element={<Thread />} />
          <Route path="/people" element={<People />} />
          <Route path="/people/:id" element={<Person />} />
          <Route path="/pipelines" element={<Pipelines />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/properties/:id" element={<Property />} />
          <Route path="/listings" element={<Listings />} />
          <Route path="/listings/:id" element={<Listing />} />
          {staff && <Route path="/duplicates" element={<Duplicates />} />}
          {owner && <Route path="/imports" element={<Imports />} />}
          {owner && <Route path="/imports/:id" element={<ImportRun />} />}
          {owner && <Route path="/settings" element={<Settings />} />}
          <Route path="/more" element={<More />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
    </MeProvider>
  );
}

// One root, even when the dev server hot-reloads this module.
const w = window as unknown as { __gcrmRoot?: ReturnType<typeof createRoot> };
w.__gcrmRoot ??= createRoot(document.getElementById("root")!);
w.__gcrmRoot.render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <ToastProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
